import { StarFourIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import z from 'zod'

import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { MarketShopInventoryItem, MarketShopInventoryItemStatusEnum } from '@/models'
import { getMarketShopImageHref } from '@/pages/market-shop/market-shop.utils'
import { formatPrice } from '@/utils'

import * as S from './market-shop-inventory.styled'

export const getMarketShopInventoryColumns = (
  t: TFunction<['market-shop-dashboard']>,
): TableColumn<MarketShopInventoryItem>[] => [
  {
    id: 'name',
    render: (item) => (
      <S.ItemNameCell>
        <S.AvatarNameContainer>
          <Avatar
            src={item.image ? getMarketShopImageHref(item.image) : undefined}
            alt={item.summary}
            fallbackIcon={StarFourIcon}
            fallbackIconColor="gray600"
            shape="square"
            size="small"
          />
          <S.ItemNameText
            as="span"
            color="black700"
            size="text-small"
            weight="medium"
            breakLines={1}
          >
            {item.summary}
          </S.ItemNameText>
        </S.AvatarNameContainer>
      </S.ItemNameCell>
    ),
    sortKey: 'name',
    title: t('market-shop-dashboard:inventory.columns.itemName'),
    width: '28rem',
  },
  {
    id: 'description',
    render: (item) => item.description || '-',
    title: t('market-shop-dashboard:inventory.columns.description'),
    width: '24rem',
  },
  {
    id: 'tag',
    render: (item) => {
      if (item.tags.length === 0) return '-'
      return (
        <S.TagsContainer>
          {item.tags.map((tag) => {
            if (tag.name === 'Discount') {
              return (
                <Badge key={tag.id} color="orange" size="small">
                  -{tag.discount}%
                </Badge>
              )
            }
            return (
              <Badge key={tag.id} color="green" size="small">
                {tag.name === 'NEW' ? t('market-shop-dashboard:inventory.tagNewLabel') : tag.name}
              </Badge>
            )
          })}
        </S.TagsContainer>
      )
    },
    title: t('market-shop-dashboard:inventory.columns.tag'),
    width: '16rem',
  },
  {
    id: 'sku',
    render: (item) => item.sku || '-',
    title: t('market-shop-dashboard:inventory.columns.sku'),
    width: '12rem',
  },
  {
    id: 'category',
    render: (item) =>
      item.categoryId?.name ?? t('market-shop-dashboard:inventory.allCategoryLabel'),
    title: t('market-shop-dashboard:inventory.columns.category'),
    width: '14rem',
  },
  {
    id: 'price',
    render: (item) => (
      <S.PriceContainer>
        {item.discountedPrice !== item.price && (
          <S.OriginalPrice>{formatPrice(item.price, { zeroPrice: true })}</S.OriginalPrice>
        )}
        <span>{formatPrice(item.discountedPrice, { zeroPrice: true })}</span>
      </S.PriceContainer>
    ),
    title: t('market-shop-dashboard:inventory.columns.price'),
    width: '10rem',
  },
  {
    id: 'inStock',
    render: (item) => {
      if (item.unlimited) {
        return (
          <Badge color="purple" size="small" variant="light">
            {t('market-shop-dashboard:inventory.unlimitedBadge')}
          </Badge>
        )
      }
      if (item.quantity === 0) {
        return (
          <Badge color="red" size="small" variant="light">
            {t('market-shop-dashboard:inventory.outOfStockBadge')}
          </Badge>
        )
      }
      if (item.quantity <= 10) {
        return (
          <Badge color="red" size="small" variant="light">
            {t('market-shop-dashboard:inventory.quantityLeftBadge', { count: item.quantity })}
          </Badge>
        )
      }
      return (
        <Badge color="blue" size="small" variant="light">
          {t('market-shop-dashboard:inventory.quantityLeftBadge', { count: item.quantity })}
        </Badge>
      )
    },
    sortKey: 'inStock',
    title: t('market-shop-dashboard:inventory.columns.inStock'),
    width: '12rem',
  },
  {
    id: 'tax',
    render: (item) => `${item.tax}%`,
    title: t('market-shop-dashboard:inventory.columns.tax'),
    width: '8rem',
  },
]

export const marketShopInventoryQueryFiltersSchema = z.object({
  categoryId: z.array(z.coerce.number().or(z.coerce.string())).optional(),
  limit: z.coerce.number().optional(),
  search: z.coerce.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  status: MarketShopInventoryItemStatusEnum.optional(),
  tagId: z.array(z.coerce.number()).optional(),
})

export type MarketShopInventoryFilters = z.infer<typeof marketShopInventoryQueryFiltersSchema>

export const marketShopInventoryInitialFilters: MarketShopInventoryFilters = {
  categoryId: [],
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
  status: MarketShopInventoryItemStatusEnum.enum['active'],
  tagId: [],
}
